import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Database, Cpu, Network, Layers, HardDrive, ChevronRight } from 'lucide-react';
import './Architecture.css';

const Architecture = () => {
  const [activeLayer, setActiveLayer] = useState(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const layers = [
    {
      id: 'consensus',
      icon: <Database size={28} />,
      name: 'Consensus Layer',
      subtitle: 'Hybrid PoS + BFT',
      description: 'Validator-based consensus with deterministic finality and decentralized governance',
      details: [
        'Proposers selected via VRF (Verifiable Random Function)',
        'Validator committees rotate for block signing with BLS signatures',
        '1-2 second optimistic confirmation',
        '30 second cross-L1 anchoring finality',
        'Slashing mechanism for downtime and equivocation',
      ],
      specs: 'PoS + BFT | VRF Selection | BLS Signatures',
    },
    {
      id: 'execution',
      icon: <Cpu size={28} />,
      name: 'Execution Layer (NVM)',
      subtitle: 'Dual-Runtime Virtual Machine',
      description: 'Advanced execution environment supporting both EVM and WASM smart contracts',
      details: [
        'EVM runtime for Solidity compatibility',
        'WASM runtime for Rust and Move contracts',
        'Deterministic parallelism for financial primitives',
        'Unified gas accounting using $NOMOX',
        'Cross-runtime interoperability',
      ],
      specs: 'EVM + WASM | Parallel Execution | Gas Optimized',
    },
    {
      id: 'settlement',
      icon: <Network size={28} />,
      name: 'Settlement Layer (MLSE)',
      subtitle: 'Multi-L1 Settlement Engine',
      description: 'Proof aggregation and parallel settlement across multiple Layer-1 blockchains',
      details: [
        'Proof Aggregator: Collects and compresses ZK and Optimistic proofs',
        'Settlement Coordinator: Optimizes settlement paths across L1s',
        'Relayer Contracts: Verify and publish proofs to respective chains',
        'State Committee: Validator quorum signs checkpoints with BLS',
        'Simultaneous finality across Ethereum, BNB, Solana, and more',
      ],
      specs: 'ZK + Optimistic | Multi-L1 Anchoring | Proof Compression',
    },
    {
      id: 'liquidity',
      icon: <Layers size={28} />,
      name: 'Liquidity Routing (SLRL)',
      subtitle: 'Shared Liquidity Routing Layer',
      description: 'Cross-chain liquidity fabric enabling atomic swaps and multi-market execution',
      details: [
        'Intent Router: User signs intent, router finds optimal execution',
        'Liquidity Relayers: Execute mirrored orders across AMMs and CEXs',
        'Atomic Swaps: Secured using HTLCs (Hash Time-Locked Contracts)',
        'Multi-market execution across DeFi and centralized venues',
        'Real-time orderbook synchronization',
      ],
      specs: 'Intent-Based | Atomic Swaps | HTLC Security',
    },
    {
      id: 'infrastructure',
      icon: <HardDrive size={28} />,
      name: 'Infrastructure Layer',
      subtitle: 'DA, Oracles & Developer Tools',
      description: 'Data availability, oracle feeds, and comprehensive development infrastructure',
      details: [
        'Data Availability: Stored across DA nodes, mirrored on Ethereum',
        'Multi-tier oracles: Chainlink, Pyth, and Nomoex Exchange feeds',
        'Medianized values validated via on-chain quorum',
        'Comprehensive SDKs for TypeScript, Python, and Go',
        'Block explorers, indexers, and analytics tools',
      ],
      specs: 'DA Mirroring | Multi-Oracle | Full SDK Suite',
    },
  ];

  return (
    <section id="architecture" className="architecture-alien" data-testid="architecture-section" ref={ref}>
      <div className="section-container">
        <motion.div
          className="section-header-alien"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="section-tag">
            <div className="tag-line"></div>
            <span className="tag-text">SYSTEM ARCHITECTURE</span>
            <div className="tag-line"></div>
          </div>
          <h2 className="section-title-alien">
            <span className="title-normal">MODULAR</span>
            <span className="title-highlight">ARCHITECTURE</span>
          </h2>
          <p className="section-subtitle-alien">
            Five specialized layers working in perfect harmony for unprecedented blockchain performance
          </p>
        </motion.div>

        <div className="architecture-stack">
          {layers.map((layer, index) => (
            <motion.div
              key={layer.id}
              className={`arch-layer ${activeLayer === layer.id ? 'active' : ''}`}
              onClick={() => setActiveLayer(activeLayer === layer.id ? null : layer.id)}
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              data-testid={`layer-${layer.id}`}
            >
              <div className="layer-header">
                <div className="layer-icon-wrapper">
                  <div className="layer-icon">{layer.icon}</div>
                </div>
                <div className="layer-info">
                  <h3 className="layer-name">{layer.name}</h3>
                  <div className="layer-subtitle">{layer.subtitle}</div>
                  <p className="layer-description">{layer.description}</p>
                </div>
                <ChevronRight className={`layer-chevron ${activeLayer === layer.id ? 'rotated' : ''}`} size={24} />
              </div>

              <AnimatePresence>
                {activeLayer === layer.id && (
                  <motion.div
                    className="layer-details"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="details-divider"></div>
                    <ul className="details-list">
                      {layer.details.map((detail, idx) => (
                        <li key={idx} className="detail-item">
                          <div className="detail-bullet"></div>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="layer-specs">
                      <div className="specs-label">Technical Specs:</div>
                      <div className="specs-value">{layer.specs}</div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {index < layers.length - 1 && (
                <div className="layer-connector">
                  <div className="connector-line"></div>
                  <div className="connector-pulse"></div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          className="arch-note"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="note-icon">💡</div>
          <span>Click on any layer to explore detailed technical specifications</span>
        </motion.div>
      </div>
    </section>
  );
};

export default Architecture;